import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import "./CookieConsent.css";

const STORAGE_KEY = "nt_cookie_consent";

function loadMarketingScripts() {
    if (window.fbq) {
        window.fbq('track', 'PageView');
        return;
    }

    (function (f, b, e, v, n, t, s) {
        if (f.fbq) return;

        n = f.fbq = function () {
            n.callMethod
                ? n.callMethod.apply(n, arguments)
                : n.queue.push(arguments);
        };

        if (!f._fbq) f._fbq = n;

        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];

        t = b.createElement(e);
        t.async = true;
        t.src = v;

        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);

    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

    window.fbq('init', '1671920400560387');
    window.fbq('track', 'PageView');
}

function loadAnalyticsScripts() {
    console.log("Analytics scripts loaded after consent");
}

function getStoredConsent() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function storeConsent(value) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
}

export default function CookieConsent() {
    const { t } = useTranslation();
    const [visible, setVisible] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [analytics, setAnalytics] = useState(true);
    const [marketing, setMarketing] = useState(true);



    useEffect(() => {
        const stored = getStoredConsent();
        if (!stored) {

            const timer = setTimeout(() => setVisible(true), 3000);
            return () => clearTimeout(timer);
        } else {
            if (stored.analytics) loadAnalyticsScripts();
            if (stored.marketing) loadMarketingScripts();
        }
    }, []);

    const applyConsent = (value) => {
        storeConsent(value);
        if (value.analytics) loadAnalyticsScripts();
        if (value.marketing) loadMarketingScripts();
        setVisible(false);
    };

    const acceptAll = () =>
        applyConsent({ necessary: true, analytics: true, marketing: true });

    const rejectAll = () =>
        applyConsent({ necessary: true, analytics: false, marketing: false });

    const saveSettings = () =>
        applyConsent({ necessary: true, analytics, marketing });

    if (!visible) return null;

    return (
        <div className="cookie-consent__overlay" role="dialog" aria-live="polite" aria-label={t("cookieConsent.title")}>
            <div className="cookie-consent__card">
                <div className="cookie-consent__glow" />

                <div className="cookie-consent__body">
                    <p className="cookie-consent__eyebrow">{t("cookieConsent.eyebrow")}</p>
                    <h3 className="cookie-consent__title">{t("cookieConsent.title")}</h3>
                    <p className="cookie-consent__text">{t("cookieConsent.text")}</p>
                    <p className="cookie-consent__privacy">
                        {t("cookieConsent.privacyPre")}{" "}
                        <Link to="/privacy" className="cookie-consent__link">
                            {t("cookieConsent.privacyLinkText")}
                        </Link>{" "}
                        {t("cookieConsent.privacyPost")}
                    </p>

                    {!showSettings ? (
                        <div className="cookie-consent__actions">
                            <button
                                className="cookie-consent__btn cookie-consent__btn--primary"
                                onClick={acceptAll}
                            >
                                {t("cookieConsent.acceptAll")}
                            </button>
                            <button
                                className="cookie-consent__btn cookie-consent__btn--ghost"
                                onClick={rejectAll}
                            >
                                {t("cookieConsent.rejectAll")}
                            </button>
                            <button
                                className="cookie-consent__btn cookie-consent__btn--link"
                                onClick={() => setShowSettings(true)}
                            >
                                {t("cookieConsent.customize")}
                            </button>
                        </div>
                    ) : (
                        <div className="cookie-consent__settings">
                            <div className="cookie-consent__row">
                                <div>
                                    <span className="cookie-consent__row-title">
                                        {t("cookieConsent.necessaryTitle")}
                                    </span>
                                    <span className="cookie-consent__row-desc">
                                        {t("cookieConsent.necessaryDesc")}
                                    </span>
                                </div>
                                <label className="cookie-consent__switch cookie-consent__switch--disabled">
                                    <input
                                        className="cookie-consent__switch-input"
                                        type="checkbox"
                                        checked
                                        disabled
                                        readOnly
                                    />
                                    <span className="cookie-consent__slider" />
                                </label>
                            </div>

                            <div className="cookie-consent__row">
                                <div>
                                    <span className="cookie-consent__row-title">
                                        {t("cookieConsent.analyticsTitle")}
                                    </span>
                                    <span className="cookie-consent__row-desc">
                                        {t("cookieConsent.analyticsDesc")}
                                    </span>
                                </div>
                                <label className="cookie-consent__switch">
                                    <input
                                        className="cookie-consent__switch-input"
                                        type="checkbox"
                                        checked={analytics}
                                        onChange={(e) => setAnalytics(e.target.checked)}
                                    />
                                    <span className="cookie-consent__slider" />
                                </label>
                            </div>

                            <div className="cookie-consent__row">
                                <div>
                                    <span className="cookie-consent__row-title">
                                        {t("cookieConsent.marketingTitle")}
                                    </span>
                                    <span className="cookie-consent__row-desc">
                                        {t("cookieConsent.marketingDesc")}
                                    </span>
                                </div>
                                <label className="cookie-consent__switch">
                                    <input
                                        className="cookie-consent__switch-input"
                                        type="checkbox"
                                        checked={marketing}
                                        onChange={(e) => setMarketing(e.target.checked)}
                                    />
                                    <span className="cookie-consent__slider" />
                                </label>
                            </div>

                            <div className="cookie-consent__actions">
                                <button
                                    className="cookie-consent__btn cookie-consent__btn--primary"
                                    onClick={saveSettings}
                                >
                                    {t("cookieConsent.save")}
                                </button>
                                <button
                                    className="cookie-consent__btn cookie-consent__btn--link"
                                    onClick={() => setShowSettings(false)}
                                >
                                    {t("cookieConsent.back")}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}